// Variable → CRM field bookkeeping for the WhatsApp template editor
// (RequestTemplateDialog).
//
// The examples a template is submitted to Meta with BECOME its runtime
// context mapping (backend ``generate_template_context_mapping``). Before
// 2026-10-23 the editor sent ``var1`` for any ``{{1}}`` it had no field for —
// always the case after Edit or Clone, which started with an empty map — and
// the approved template then refused every send. So:
//   * the map is kept PER COMPONENT (header and body each have their own {{1}});
//   * Edit / Clone restore it from the template's saved mapping;
//   * a variable with no field blocks submission (no placeholder fallback).

export const FIELDS = ['headerText', 'bodyText'];
const COMPONENT_OF = { headerText: 'HEADER', bodyText: 'BODY' };

export const emptyTagMap = () => ({ headerText: {}, bodyText: {} });

// Saved mapping keys that are spelled differently from the editor's chips.
const CHIP_ALIASES = {
  lead_first_name: 'first_name',
  firstname: 'first_name',
  patient_first_name: 'first_name',
  practice_full_address: 'practice_address',
  practice_number: 'practice_phone',
  practice_telephone: 'practice_phone',
  practice_phone_number: 'practice_phone',
  current_user_first_name: 'user_first_name',
};

const humanise = (key) => String(key)
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (c) => c.toUpperCase());

// Distinct positional variable numbers in ``text``, ascending. Meta orders a
// component's examples by placeholder number, so the saved mapping's i-th key
// belongs to the i-th entry here.
export const positionalNumbers = (text = '') => {
  const nums = (text.match(/\{\{(\d+)\}\}/g) || []).map((m) => parseInt(m.slice(2, -2), 10));
  return [...new Set(nums)].sort((a, b) => a - b);
};

// A tag object for a saved key: the matching chip when there is one, else a
// field the CRM does fill that the chip bar simply does not offer
// (``form_names``, ``treatment_price`` ...) — kept so it is not lost on save.
export const tagForKey = (key, mergeTags = []) => {
  const chipKey = CHIP_ALIASES[key] || key;
  const chip = mergeTags.find((t) => t.key === chipKey) || mergeTags.find((t) => t.key === key);
  if (chip) return chip;
  return { key, label: humanise(key), icon: 'mdi:variable', example: humanise(key) };
};

/**
 * Rebuild the per-component map from a template the API returned
 * (``template_context_mapping`` + ``mapping_issues``). Positions the backend
 * flags as unfillable (``var1``, ``Sarah`` …) are left out, so the editor asks
 * the user to choose a field for them instead of resubmitting the junk.
 */
export const tagMapFromTemplate = (template, mergeTags = []) => {
  const map = emptyTagMap();
  if (!template) return map;
  const mapping = template.template_context_mapping || {};
  const flagged = new Set(
    (template.mapping_issues || []).map((i) => `${i.component}:${i.position}`)
  );
  const textOf = (type) => template.components?.find((c) => c.type === type)?.text || '';

  FIELDS.forEach((field) => {
    const component = COMPONENT_OF[field];
    const keys = Array.isArray(mapping[component]) ? mapping[component] : [];
    positionalNumbers(textOf(component)).forEach((num, idx) => {
      const key = keys[idx];
      if (typeof key !== 'string' || !key.trim()) return;
      if (flagged.has(`${component}:${idx + 1}`)) return;
      map[field][num] = tagForKey(key, mergeTags);
    });
  });
  return map;
};

/** ``[{ field, num }]`` for every positional variable that has no field yet. */
export const unmappedVariables = (form, tagMap) => FIELDS.flatMap((field) => {
  if (field === 'headerText' && form.headerFormat === 'IMAGE') return [];
  return positionalNumbers(form[field] || '')
    .filter((num) => !tagMap?.[field]?.[num]?.key)
    .map((num) => ({ field, num }));
});

/** Tag for ``{{num}}`` in either component (body first) — preview/test panel use. */
export const tagForNumber = (tagMap, num) => tagMap?.bodyText?.[num] || tagMap?.headerText?.[num] || null;

/**
 * Example values submitted to Meta for one component, in placeholder order.
 * A positional variable without a field yields ``null`` — never a ``varN``
 * placeholder; the dialog blocks submission before this can reach Meta and the
 * backend refuses it (``template_variable_unmapped``) as a second line.
 */
export const exampleValuesForText = (text, fieldTagMap) => {
  const tokens = (text.match(/\{\{(?:\d+|[a-zA-Z_][a-zA-Z0-9_]*)\}\}/g) || []).map((m) => m.slice(2, -2));
  return tokens.map((tok) => {
    if (/^\d+$/.test(tok)) return fieldTagMap?.[parseInt(tok, 10)]?.key || null;
    return tok; // named variable — the token IS the param name
  });
};

export const FIELD_LABELS = { headerText: 'Header', bodyText: 'Message' };
